import { useState, FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Field, FieldGroup, FieldLabel, FieldSet,
} from '@/components/ui/field'
import { SearchableSelect } from '@/components/SearchableSelect'
import { DatePicker } from '@/components/DatePicker'
import { LanguagePicker } from '@/components/LanguagePicker'
import { extractErr } from '@/components/ui/inline-edit'

type Category = {
  category: string
  label: string
  source_type: string
  schema: string[]
}

type Template = {
  id: number
  category: string
  category_label: string
  source_type: string
  subsidiary: number | null
  subsidiary_code: string | null
  subsidiary_name: string | null
  language_code: string
  version: number
  effective_from: string
  effective_until: string | null
  name: string
  description: string
  format: string
  active: boolean
  created_at: string
  updated_at: string
}

type Subsidiary = {
  id: number
  code: string
  name: string
}

export default function DocumentTemplates() {
  const qc = useQueryClient()
  const navigate = useNavigate()

  const templatesQ = useQuery({
    queryKey: ['document-templates'],
    queryFn: async () => {
      const r = await api.get('/document-templates/')
      const body = r.data
      if (Array.isArray(body)) return body as Template[]
      if (body && Array.isArray(body.results)) return body.results as Template[]
      return [] as Template[]
    },
  })

  const categoriesQ = useQuery({
    queryKey: ['document-templates', 'categories'],
    queryFn: async () =>
      (await api.get<Category[]>('/document-templates/categories/')).data,
  })

  const subsidiariesQ = useQuery({
    queryKey: ['subsidiaries'],
    queryFn: async () => {
      const r = await api.get('/subsidiaries/')
      const body = r.data
      if (Array.isArray(body)) return body as Subsidiary[]
      if (body && Array.isArray(body.results)) return body.results as Subsidiary[]
      return [] as Subsidiary[]
    },
  })

  const [creating, setCreating] = useState(false)

  const createMut = useMutation({
    mutationFn: async (payload: any) =>
      (await api.post('/document-templates/', payload)).data as Template,
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['document-templates'] })
      setCreating(false)
      navigate(`/admin/document-templates/${t.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/document-templates/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['document-templates'] }),
    onError: (e) => toast.error(extractErr(e)),
  })

  const templates = templatesQ.data || []
  const categories = categoriesQ.data || []
  const subsidiaries = subsidiariesQ.data || []

  // Group by category, then sort by subsidiary (global first), language, version desc.
  const grouped: Record<string, Template[]> = {}
  templates.forEach((t) => {
    if (!grouped[t.category]) grouped[t.category] = []
    grouped[t.category].push(t)
  })
  Object.keys(grouped).forEach((cat) => {
    grouped[cat].sort((a, b) => {
      const aSub = a.subsidiary_code || ''
      const bSub = b.subsidiary_code || ''
      if (aSub !== bSub) return aSub.localeCompare(bSub)
      if (a.language_code !== b.language_code)
        return a.language_code.localeCompare(b.language_code)
      return b.version - a.version
    })
  })

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Document Templates</h2>
          <p className="text-sm text-muted-foreground">
            Templates render against a data provider per category, with subsidiary →
            global and language → default fallback. Versions stay frozen on issued
            documents.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>+ New template</Button>
      </div>

      {templatesQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : templates.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-2">No templates yet.</p>
          <p className="text-sm text-muted-foreground">
            Click "New template" to start one. Templates resolve in order:
            (subsidiary + language) → (subsidiary, any language) → (global, language)
            → (global, any language).
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {categories
            .filter((c) => grouped[c.category])
            .map((c) => (
              <div key={c.category} className="bg-card rounded-lg shadow overflow-hidden">
                <div className="px-4 py-2 bg-muted/30 border-b">
                  <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{c.label}</div>
                  <div className="text-xs text-muted-foreground font-mono">{c.category} · binds to {c.source_type}</div>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground border-b">
                    <tr>
                      <th className="text-left px-4 py-2">Subsidiary</th>
                      <th className="text-left px-4 py-2">Language</th>
                      <th className="text-right px-4 py-2">Version</th>
                      <th className="text-left px-4 py-2">Effective</th>
                      <th className="text-center px-4 py-2">Active</th>
                      <th className="text-right px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {grouped[c.category].map((t) => (
                      <tr key={t.id} className="hover:bg-muted/30">
                        <td className="px-4 py-2">
                          {t.subsidiary_code || <span className="italic text-muted-foreground">Global</span>}
                        </td>
                        <td className="px-4 py-2 font-mono uppercase">
                          {t.language_code || <span className="italic text-muted-foreground lowercase">any</span>}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">v{t.version}</td>
                        <td className="px-4 py-2 text-muted-foreground text-xs">
                          {t.effective_from}{t.effective_until ? ` → ${t.effective_until}` : ''}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {t.active
                            ? <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">on</span>
                            : <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">off</span>}
                        </td>
                        <td className="px-4 py-2 text-right space-x-3">
                          <Link
                            to={`/admin/document-templates/${t.id}`}
                            className="text-blue-600 hover:underline text-sm"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => { if (confirm('Delete this template version?')) deleteMut.mutate(t.id) }}
                            className="text-red-600 hover:underline text-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          {/* Show categories with NO templates yet */}
          {categories.filter((c) => !grouped[c.category]).length > 0 && (
            <div className="bg-card rounded-lg shadow p-4">
              <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2">
                Categories with no templates yet
              </div>
              <div className="flex flex-wrap gap-2">
                {categories
                  .filter((c) => !grouped[c.category])
                  .map((c) => (
                    <span key={c.category} className="text-xs px-2 py-1 rounded border border-dashed text-muted-foreground">
                      {c.label}
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {creating && (
        <CreateDialog
          categories={categories}
          subsidiaries={subsidiaries}
          submitting={createMut.isPending}
          onCancel={() => setCreating(false)}
          onSubmit={(payload) => createMut.mutate(payload)}
        />
      )}
    </AdminLayout>
  )
}

function CreateDialog({
  categories, subsidiaries, onSubmit, onCancel, submitting,
}: {
  categories: Category[]
  subsidiaries: Subsidiary[]
  onSubmit: (payload: any) => void
  onCancel: () => void
  submitting: boolean
}) {
  const [form, setForm] = useState({
    category: undefined as string | undefined,
    subsidiary: undefined as string | undefined,
    language_code: 'en',
    version: 1,
    effective_from: new Date().toISOString().slice(0, 10),
    name: '',
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!form.category || !form.effective_from) return
    onSubmit({
      category: form.category,
      subsidiary: form.subsidiary ? Number(form.subsidiary) : null,
      language_code: form.language_code.trim().toLowerCase(),
      version: Number(form.version),
      effective_from: form.effective_from,
      name: form.name,
      body_html: '<p>Write your template here.</p>',
      format: 'HTML',
      active: true,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-40">
      <div className="bg-card rounded-lg shadow-2xl max-w-lg w-full p-6">
        <h3 className="text-lg font-semibold mb-3">New document template</h3>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldGroup>
              <Field>
                <FieldLabel>Category</FieldLabel>
                <SearchableSelect
                  value={form.category}
                  onChange={(v) => setForm({ ...form, category: v })}
                  placeholder="Pick a category…"
                  options={categories.map((c) => ({
                    value: c.category, label: c.label, hint: c.category,
                  }))}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel>Subsidiary</FieldLabel>
                  <SearchableSelect
                    value={form.subsidiary ?? '__none__'}
                    onChange={(v) => setForm({ ...form, subsidiary: v === '__none__' ? undefined : v })}
                    placeholder="Global"
                    options={[
                      { value: '__none__', label: 'Global (no subsidiary)' },
                      ...subsidiaries.map((s) => ({
                        value: String(s.id), label: s.name, hint: s.code,
                      })),
                    ]}
                  />
                </Field>
                <Field>
                  <FieldLabel>Language</FieldLabel>
                  <LanguagePicker
                    value={form.language_code}
                    onChange={(code) => setForm({ ...form, language_code: code })}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel>Version</FieldLabel>
                  <Input
                    type="number" min={1}
                    value={form.version}
                    onChange={(e) => setForm({ ...form, version: Number(e.target.value || 1) })}
                  />
                </Field>
                <Field>
                  <FieldLabel>Effective from</FieldLabel>
                  <DatePicker
                    required
                    value={form.effective_from}
                    onChange={(v) => setForm({ ...form, effective_from: v })}
                  />
                </Field>
              </div>
              <Field>
                <FieldLabel>Name (optional)</FieldLabel>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. 'Standard Offer Letter — Germany'"
                />
              </Field>
            </FieldGroup>
          </FieldSet>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={!form.category || !form.effective_from || submitting}>
              {submitting ? 'Creating…' : 'Create & edit'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
